import { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import authHeader from "services/auth-header";
import API from "services/axios";
import { convertSqlTimestampToDate, truncateString } from "utils/UtilityFuncs";
import TableOnly from "components/DataTable/TableOnly";
import PaginationWithState from "components/Pagination/PaginationWithState";
import { useNavigate } from "react-router-dom";

export default function ShipmentHistoryTab({
  shipment,
  setActiveTab,
  setShipment,
  addShipment,
}) {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Pagination states
  const [pageLimit, setPageLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // Fetch shipments from API
  const getShipments = async (query) => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/sales/shipments",
        {
          page: pageNumber,
          limit: pageLimit,
          filterBy: "all",
          query: query?.trim(),
        },
        { headers: authHeader() },
      );
      if (data.success) {
        setShipments(data.data || []);
        setTotalPages(data.totalPages || 0);
      }
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  // Fetch shipments from API
  const voidShipment = async (shipmentId) => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/sales/shipments/void-shipment",
        { shipmentId: shipmentId },
        { headers: authHeader() },
      );
      if (data.success) {
        await getShipments();
      }
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (searchText && searchText !== "") {
        getShipments(searchText);
      } else {
        getShipments("");
      }
    }, 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageLimit]);

  /*   Table functions
   ********************************************* */
  const table_head = [
    { name: "S-ID", value: "shipmentId" },
    { name: "Created At", value: "createdAt" },
    { name: "Customer", value: "customerName" },
    { name: "Status", value: "shipmentStatus" },
    { name: "Tracking No", value: "trackingNo" },
    { name: "Shipment Info", value: "shippingAddress" },
    { name: "Action", value: "Actions" },
  ];

  const table_body = (ship, index) => {
    // Parse shipping address
    let addr = ship.shippingAddress;

    // convert addr to JSON until addr is an object
    try {
      addr = JSON.parse(addr);
      while (typeof addr === "string") {
        addr = JSON.parse(addr);
      }
    } catch (e) {
      addr = {};
    }

    // Build single-line address string
    const company =
      addr?.addressCompany &&
      ship?.customerName?.toLowerCase() === addr?.addressCompany?.toLowerCase()
        ? ""
        : addr?.addressCompany
          ? addr.addressCompany + ", "
          : "";
    const line1 = addr?.addressLine1 ? addr.addressLine1 + ", " : "";
    const line2 = addr?.addressLine2 ? addr.addressLine2 + ", " : "";
    const city = addr?.city ? addr.city + ", " : "";
    const state = addr?.state ? addr.state + ", " : "";
    const zip = addr?.zip ? addr.zip + ", " : "";
    const country = addr?.country ? addr.country + ", " : "";

    let customerName = ship?.customer?.customerName || "";

    const handleViewCustomer = (customerId) => {
      navigate(`/customer/details/${customerId}`);
    };

    return (
      <tr
        key={ship.shipmentId}
        className={ship.isVoided ? "text-decoration-line-through" : ""}
      >
        <td className="align-middle py-1">S-{ship.shipmentId}</td>
        <td className="align-middle py-1">
          {convertSqlTimestampToDate(ship.createdAt)}
        </td>
        <td className="align-middle py-1 text-truncate">
          <div className="d-flex align-items-center">
            <div>
              <button
                onClick={() => handleViewCustomer(ship?.customer?.customerId)}
                className="btn btn-sm btn-info py-0 px-1 text-white me-1"
              >
                View
              </button>
            </div>
            <div>{customerName}</div>
          </div>
        </td>
        <td className="align-middle py-1">{ship.shipmentStatus}</td>
        <td className="align-middle py-1">{ship.trackingNo || ""}</td>
        <td className="align-middle py-1">
          <span>
            {truncateString(
              `${company} 
            ${line1} 
            ${line2} 
            ${city} 
            ${state} 
            ${zip} 
            ${country}`,
              100,
            )}
          </span>
        </td>
        <td className="align-middle py-1 text-center table-action">
          <button
            onClick={(e) => {
              voidShipment(shipment?.shipmentId);
            }}
            className={[
              "btn btn-info btn-sm me-1",
              ship.isVoided ? "disabled" : "",
            ].join(" ")}
          >
            {ship?.isVoided ? "Voided" : "Void"}
          </button>
          <Button
            size="sm"
            variant="success"
            onClick={() => {
              setShipment(ship);
              setActiveTab("shipment-details");
            }}
          >
            Select
          </Button>
        </td>
      </tr>
    );
  };

  return (
    <div className="p-2">
      {loading && <Spinner />}

      {/* Search and Actions Bar */}
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div className="ps-2">
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
              <li onClick={() => setPageLimit(30)}>
                <button className="dropdown-item">30</button>
              </li>
              <li onClick={() => setPageLimit(50)}>
                <button className="dropdown-item">50</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchText && searchText !== "") {
                getShipments(searchText);
              } else {
                getShipments("");
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search here..."
              value={searchText}
              className="form-control"
              onChange={(e) => setSearchText(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchText !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchText("");
                  setPageNumber(1);
                  getShipments("");
                }}
              >
                Clear
              </button>
            )}
          </form>

          {/* Add Shipment button */}
          <div className="">
            <Button
              variant="primary"
              className="ms-1"
              onClick={addShipment}
              disabled={adding}
            >
              {adding ? "Adding..." : "Add Shipment"}
            </Button>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div
        className="px-1 flex-grow-1 d-flex"
        style={{ minHeight: "70vh", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={shipments}
            disableSorting={false}
            actionsOnEnd={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>
    </div>
  );
}
